import { Injectable } from '@angular/core';
import { ApolloSchoolService } from '@core/graphql/apolloSchool.service';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface PackageLimits {
  studentLimit: number | null; // null means no limit
  classLimit: number | null; // null means no limit
  isTrialExpired: boolean;
  package: string;
}

@Injectable({
  providedIn: 'root'
})
export class PackageLimitService {
  private schoolConfigurationCache: any = null;
  private schoolIdCache: number | null = null;

  constructor(private schoolService: ApolloSchoolService) {}

  //Get school configuration with package info
  getSchoolConfiguration(schoolId: number): Observable<any> {
    // Return cached if same school
    if (this.schoolIdCache === schoolId && this.schoolConfigurationCache) {
      return of(this.schoolConfigurationCache);
    }

    return this.schoolService.getSchoolById(schoolId).pipe(
      map((school: any) => {
        const config = school?.schoolConfiguration;
        
        if (config) {
          this.schoolConfigurationCache = config;
          this.schoolIdCache = schoolId;
        } else {
          console.error('[PackageLimitService] No schoolConfiguration found for schoolId:', schoolId);
        }
        return config;
      }),
      catchError((error) => {
        console.error('[PackageLimitService] Error fetching school configuration:', error);
        return of(null);
      })
    );
  }

  //Check if package is trial
  isTrialPackage(packageName: string | null | undefined): boolean {
    return (packageName || '').toLowerCase() === 'trial';
  }

  //Check if package is basic
  isBasicPackage(packageName: string | null | undefined): boolean {
    return (packageName || '').toLowerCase() === 'basic';
  }

  //Check if package is standard
  isStandardPackage(packageName: string | null | undefined): boolean {
    return (packageName || '').toLowerCase() === 'standard';
  }

  //Check if package is premium
  isPremiumPackage(packageName: string | null | undefined): boolean {
    return (packageName || '').toLowerCase() === 'premium';
  }

  //Check if trial period has expired (1 month from trialPeriod date)
  //Also used for Basic package - checks if 1 month has passed since subscription start date
  isTrialExpired(trialPeriod: string | Date | null | undefined): boolean {
    if (!trialPeriod) return false;
    
    // Parse date string to local date (YYYY-MM-DD format)
    let trialStartDate: Date;
    if (typeof trialPeriod === 'string') {
      const dateParts = trialPeriod.split('T')[0].split('-');
      if (dateParts.length === 3) {
        trialStartDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
      } else {
        trialStartDate = new Date(trialPeriod);
      }
    } else {
      trialStartDate = new Date(trialPeriod);
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    trialStartDate.setHours(0, 0, 0, 0);
    
    // Calculate trial end date (1 month after trial start)
    const trialEndDate = new Date(trialStartDate);
    trialEndDate.setMonth(trialEndDate.getMonth() + 1);
    trialEndDate.setHours(0, 0, 0, 0);
    
    // Trial is expired if today is after the trial end date
    const isExpired = today > trialEndDate;    
    return isExpired;
  }

  // Get package limits for students and classes
  getPackageLimits(config: any): PackageLimits {
    if (!config) {
      return {
        studentLimit: null,
        classLimit: null,
        isTrialExpired: false,
        package: 'premium'
      };
    }

    const packageName = (config.package || '').toLowerCase();
    const isTrial = this.isTrialPackage(packageName);
    const isBasic = this.isBasicPackage(packageName);
    const isStandard = this.isStandardPackage(packageName);
    const isPremium = this.isPremiumPackage(packageName);

    // Premium and Standard have no limits
    if (isPremium || isStandard) {
      return {
        studentLimit: null,
        classLimit: null,
        isTrialExpired: false,
        package: packageName
      };
    }

    // Trial and Basic have limits
    if (isTrial || isBasic) {
      // For both Trial and Basic, check if 1 month has passed since TrialPeriod (subscription start date)
      const isExpired = this.isTrialExpired(config.trialPeriod);
      
      // Use studentCapping from School Configuration instead of hardcoded limit
      const studentLimit = config.studentCapping != null && config.studentCapping > 0 
        ? config.studentCapping 
        : null; // If studentCapping is 0 or null, treat as no limit
      
      return {
        studentLimit: studentLimit,
        classLimit: 1,
        isTrialExpired: isExpired,
        package: packageName
      };
    }

    // Default to premium (no limits)
    return {
      studentLimit: null,
      classLimit: null,
      isTrialExpired: false,
      package: 'premium'
    };
  }

  //Check if student creation is allowed
  canCreateStudent(currentStudentCount: number, config: any): { allowed: boolean; message?: string } {
    // If config is null, we cannot determine the package limits - block creation for safety
    if (!config) {
      return {
        allowed: false,
        message: 'Cannot create student: School configuration is missing. Please contact support.'
      };
    }
    
    const limits = this.getPackageLimits(config);
    
    if (limits.studentLimit === null) {
      return { allowed: true };
    }

    if (currentStudentCount >= limits.studentLimit) {
      return {
        allowed: false,
        message: `Cannot create student: Maximum limit of ${limits.studentLimit} students reached for ${limits.package} package.`
      };
    }

    if (limits.isTrialExpired && limits.package === 'trial') {
      return {
        allowed: false,
        message: 'Cannot create student: Trial period has expired. Please upgrade your plan to continue.'
      };
    }

    return { allowed: true };
  }

  //Check if class creation is allowed
  canCreateClass(currentClassCount: number, config: any): { allowed: boolean; message?: string } {
    // If config is null, we cannot determine the package limits - block creation for safety
    if (!config) {
      return {
        allowed: false,
        message: 'Cannot create class: School configuration is missing. Please contact support.'
      };
    }
    
    const limits = this.getPackageLimits(config);
    
    if (limits.classLimit === null) {
      return { allowed: true };
    }

    if (currentClassCount >= limits.classLimit) {
      return {
        allowed: false,
        message: `Cannot create class. Maximum limit of ${limits.classLimit} class reached for ${limits.package} package.`
      };
    }

    if (limits.isTrialExpired && limits.package === 'trial') {
      return {
        allowed: false,
        message: 'Cannot create class: Trial period has expired. Please upgrade your plan to continue.'
      };
    }

    return { allowed: true };
  }

  //Clear cache (call when school changes)
  clearCache(): void {
    this.schoolConfigurationCache = null;
    this.schoolIdCache = null;
  }
}